import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { maxTextFor, type ChannelType } from '@sac/contracts'
import { api, ApiError, type SuggestionDTO, type StoredSuggestionDTO, type CertaintyDTO } from '../../lib/api'
import { qk } from '../../lib/query'
import { useSuggestionFeedback } from './useSuggestionFeedback'
import { Icon } from '../icons'
import { useToast } from '../ui/Toaster'

/** The stored row and a freshly generated one carry the same fields the card renders. */
function fromStored(s: StoredSuggestionDTO): SuggestionDTO {
  return {
    kind: s.kind === 'error' ? 'answer' : s.kind,
    gapQuestion: s.gapQuestion,
    missingFacts: s.missingFacts,
    confidence: s.confidence ?? 0,
    suggestion: s.suggestion,
    // The stored row keeps entry ids only; the card uses them for feedback, not for display.
    entries: s.entryIds.map((id) => ({ id, scope: '', sku: null, question: '', answer: '', confidence: 0, via: 'sku' as const })),
    sku: s.sku,
    violations: s.violations,
    ungrounded: s.ungrounded,
    ungroundedClaims: s.ungroundedClaims ?? [],
    blocked: s.blocked,
    servedModel: s.model ?? '',
    costUsd: 0,
    certainty: { value: s.certainty, band: s.certaintyBand, reasons: s.certaintyReasons },
  }
}

const BAND_LABEL: Record<'low' | 'medium' | 'high', string> = { low: 'baixa', medium: 'média', high: 'alta' }

/**
 * The certainty badge.
 *
 * Deliberately labeled "não validado" on every single render, not just on first use. This is a
 * Phase A structural score — see certainty.ts — and nothing has yet checked whether a reply
 * scored 70 here is actually right 70% of the time. That check needs real volume (roughly 200
 * labeled examples per category) this system does not have yet. Until it runs, the badge must
 * keep saying so: the day this becomes silently trustworthy-looking is the day someone starts
 * treating an unvalidated number as a fact, which is the exact failure this whole feature exists
 * to avoid walking into blind.
 */
function CertaintyBadge({ certainty }: { certainty: CertaintyDTO }) {
  if (certainty.value === null) {
    return (
      <span className="cert-badge na" title="Esta resposta não é uma afirmação a ser avaliada — não recebe nota.">
        certeza: N/A
      </span>
    )
  }
  const band = certainty.band ?? 'low'
  return (
    <span
      className={`cert-badge ${band}`}
      title={`Não validado ainda. Fatores: ${certainty.reasons.join(' · ')}`}
    >
      certeza: {certainty.value}% ({BAND_LABEL[band]}) · não validado
    </span>
  )
}

/**
 * The AI suggestion, sitting above the composer.
 *
 * Three outcomes, and all three are recorded: Enviar (the suggestion was right), Editar e
 * enviar (the agent's correction becomes new knowledge — the most valuable case), Descartar
 * (the suggestion was wrong, demote what produced it).
 *
 * There is no auto-send and no "always accept". The human decision is simultaneously the
 * safety boundary and the only training signal the system has.
 */
export function SuggestionCard({
  conversationId,
  channelType,
  onUse,
  onFlip,
}: {
  conversationId: string
  /** The cap an edited reply must still fit inside. */
  channelType: ChannelType
  /** Hands the final text to the normal send path — this component never sends directly. */
  onUse: (text: string, verbatim: boolean) => void
  /** Turn the card over to the manual composer. Absent when there is no back to turn to. */
  onFlip?: () => void
}) {
  const [data, setData] = useState<SuggestionDTO | null>(null)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [taught, setTaught] = useState('')
  const [beforePolish, setBeforePolish] = useState<string | null>(null)
  const toast = useToast()
  const polish = useMutation({
    mutationFn: (text: string) => api.polish(conversationId, text),
    onSuccess: (r, text) => {
      setBeforePolish(text)
      setDraft(r.text)
    },
    onError: (e: Error) => toast(e instanceof ApiError && e.detail ? e.detail : 'Não consegui melhorar o texto agora.'),
  })

  // The suggestion is generated when the CUSTOMER writes, so opening a conversation is a plain
  // read: no model call, no spinner, no cost, and the same answer every time it is opened.
  const { data: stored } = useQuery({
    queryKey: qk.storedSuggestion(conversationId),
    queryFn: () => api.storedSuggestion(conversationId),
  })

  useEffect(() => {
    // Reset when switching conversations, then adopt whatever was pre-computed for this one.
    setEditing(false)
    setBeforePolish(null)
    setTaught('')
    setError(stored?.error ?? null)
    setData(stored && stored.kind !== 'error' ? fromStored(stored) : null)
    setDraft(stored?.suggestion ?? '')
  }, [conversationId, stored])

  const generate = useMutation({
    mutationFn: () => api.suggest(conversationId),
    onSuccess: (d) => {
      setData(d)
      setDraft(d.suggestion)
      setEditing(false)
      setError(null)
    },
    onError: (e: Error) => setError(e.message),
  })

  // Teach, then immediately re-ask: the agent should see the answer their fact unlocked, not
  // have to remember to press the button again.
  const teach = useMutation({
    mutationFn: async () => {
      await api.teach(conversationId, {
        question: data?.gapQuestion ?? '',
        answer: taught,
        sku: data?.sku ?? null,
        scope: data?.sku ? 'product' : 'catalog',
      })
      return api.suggest(conversationId)
    },
    onSuccess: (d) => {
      setData(d)
      setDraft(d.suggestion)
      setTaught('')
    },
    onError: (e: Error) => setError(e.message),
  })

  const { send: sendFeedback } = useSuggestionFeedback(conversationId)

  // Editing is where a suggestion most easily grows past the cap: the agent adds a sentence to
  // one that was already close. The generator itself has never exceeded a limit in 33 measured
  // suggestions, so this guards the human edit, not the model.
  const limit = maxTextFor(channelType)
  const editOver = limit !== null && draft.trim().length > limit

  const feedback = (outcome: 'sent_verbatim' | 'edited' | 'rejected', finalText?: string) => {
    if (!data) return
    // Reports against the card the agent is actually looking at, which may be a freshly
    // generated one rather than the stored row the hook holds.
    sendFeedback(outcome, finalText ?? null, {
      suggestion: data.suggestion, entryIds: data.entries.map((e) => e.id),
      model: data.servedModel, certainty: data.certainty.value, certaintyBand: data.certainty.band,
    })
  }

  if (!data) {
    return (
      <div className="sug-launch">
        <button className="btn sec sm" disabled={generate.isPending} onClick={() => generate.mutate()}>
          <Icon name="spark" size={13} />
          {generate.isPending ? 'Consultando a base…' : 'Sugerir resposta'}
        </button>
        {error && <span className="sug-err">{error}</span>}
      </div>
    )
  }

  // The shop spoke last and nothing is pending — there is no text to offer, just a status. A
  // manual "Sugerir resposta" is still available in case the agent wants to write ahead anyway.
  if (data.kind === 'waiting_customer') {
    return (
      <div className="sug-launch">
        <span className="sug-waiting">
          <Icon name="spark" size={13} />
          Aguardando resposta de cliente
        </span>
        <button className="btn ghost sm" disabled={generate.isPending} onClick={() => generate.mutate()}>
          {generate.isPending ? 'Consultando a base…' : 'Sugerir mesmo assim'}
        </button>
        {error && <span className="sug-err">{error}</span>}
      </div>
    )
  }

  // The customer sent a photo, audio or video with no words. The model cannot see it, so there
  // is deliberately nothing to offer — saying so is more useful than a generated guess, and the
  // image itself is right there in the timeline above.
  if (data.kind === 'needs_human') {
    return (
      <div className="sug-launch">
        <span className="sug-waiting">
          <Icon name="img" size={13} />
          Cliente enviou uma mídia sem texto — veja acima e responda
        </span>
        <button className="btn ghost sm" disabled={generate.isPending} onClick={() => generate.mutate()}>
          {generate.isPending ? 'Consultando a base…' : 'Sugerir mesmo assim'}
        </button>
        {error && <span className="sug-err">{error}</span>}
      </div>
    )
  }

  // The customer wrote again after this was produced, so it answers the previous message. Shown
  // rather than hidden: a slightly-behind suggestion is often still useful, and silently
  // dropping it would look like the feature failed.
  const staleBanner = stored?.stale ? (
    <div className="sug-note">
      O cliente escreveu de novo depois desta sugestão.{' '}
      <button className="linkish" onClick={() => generate.mutate()}>
        Gerar novamente
      </button>
    </div>
  ) : null

  // The AI is asking the team, not the customer. Nothing customer-facing was produced, so this
  // is a different card entirely: no send button, one input, and the answer becomes knowledge.
  if (data.kind === 'internal_gap') {
    return (
      <div className="sug gap">
        <div className="sug-h">
          <Icon name="spark" size={13} />
          <span>A IA não sabe responder</span>
          {data.sku && <span className="sug-sku">SKU {data.sku}</span>}
          <CertaintyBadge certainty={data.certainty} />
          <button className="sug-x" onClick={() => { feedback('rejected'); setData(null) }} title="Fechar">
            <Icon name="x" size={13} />
          </button>
        </div>
        <div className="gap-q">{data.gapQuestion}</div>
        {data.missingFacts.length > 0 && (
          <div className="gap-facts">Falta: {data.missingFacts.join(' · ')}</div>
        )}
        <textarea
          className="sug-edit"
          rows={3}
          placeholder="Responda aqui e a IA aprende — vale para as próximas conversas deste produto."
          value={taught}
          onChange={(e) => setTaught(e.target.value)}
        />
        <div className="sug-actions">
          <button
            className="btn pri sm"
            disabled={!taught.trim() || teach.isPending}
            onClick={() => teach.mutate()}
          >
            {teach.isPending ? 'Salvando…' : 'Ensinar e gerar resposta'}
          </button>
          <button className="btn ghost sm" onClick={() => { feedback('rejected'); setData(null) }}>
            Responder manualmente
          </button>
        </div>
      </div>
    )
  }

  const blocked = data.blocked

  return (
    <div className={`sug ${blocked ? 'blocked' : ''}`}>
      <div className="sug-h">
        <Icon name="spark" size={13} />
        <span>{data.kind === 'poke' ? 'Retomar contato' : 'Sugestão da IA'}</span>
        {data.sku && <span className="sug-sku">SKU {data.sku}</span>}
        <CertaintyBadge certainty={data.certainty} />
        <button className="sug-x" onClick={() => { feedback('rejected'); setData(null) }} title="Descartar">
          <Icon name="x" size={13} />
        </button>
      </div>

      {staleBanner}
      {editing ? (
        <>
          <textarea className="sug-edit" value={draft} onChange={(e) => setDraft(e.target.value)} rows={5} autoFocus />
          {limit !== null && draft.trim().length > limit * 0.75 && (
            <div className={`sug-count ${editOver ? 'over' : ''}`}>
              {draft.trim().length}/{limit} caracteres
              {editOver && ' — o canal recusa acima disso'}
            </div>
          )}
        </>
      ) : (
        <div className="sug-body">{data.suggestion}</div>
      )}

      {/* Blocking problems are shown in full rather than silently sanitised: the agent needs to
          know WHY, both to fix it and to distrust the next one like it. */}
      {data.violations.some((v) => v.severity === 'block') && (
        <div className="sug-warn">
          <b>Bloqueado — regra do marketplace:</b>
          {data.violations.filter((v) => v.severity === 'block').map((v, i) => (
            <div key={i}>• {v.detail} <code>{v.match}</code></div>
          ))}
        </div>
      )}
      {data.violations.some((v) => v.severity === 'warn') && (
        <div className="sug-note">
          {data.violations.filter((v) => v.severity === 'warn').map((v, i) => (
            <div key={i}>• {v.detail} <code>{v.match}</code></div>
          ))}
        </div>
      )}
      {/* The failure with no digits in it. Kept visually distinct from the numeric warning
          because the fix differs: a wrong number needs correcting, an invented property needs
          removing outright and probably teaching. */}
      {data.ungroundedClaims.length > 0 && (
        <div className="sug-warn">
          <b>Afirmações sem fonte:</b> {data.ungroundedClaims.join(', ')} — a IA afirmou isso sem
          nenhum conhecimento que sustente. Não envie sem confirmar: pergunta do cliente não é fonte.
        </div>
      )}
      {data.ungrounded.length > 0 && (
        <div className="sug-warn">
          <b>Números sem fonte:</b> {data.ungrounded.join(', ')} — não aparecem em nenhum conhecimento
          recuperado nem na conversa. Confira antes de enviar.
        </div>
      )}

      {/* Citations: the agent sees exactly which knowledge produced the answer, so a wrong
          citation is visible instead of buried. A poke isn't answering anything, so neither the
          citation list nor the "no knowledge found" warning apply to it. */}
      {data.kind !== 'poke' &&
        (data.entries.length > 0 ? (
          <details className="sug-src">
            <summary>{data.entries.length} fonte(s) usada(s)</summary>
            {data.entries.map((e) => (
              <div className="sug-src-item" key={e.id}>
                <span className={`scope ${e.scope}`}>{e.scope}</span>
                {e.sku && <span className="sku">{e.sku}</span>}
                <span className="q">{e.question}</span>
              </div>
            ))}
          </details>
        ) : (
          <div className="sug-warn">
            Nenhum conhecimento encontrado para esta pergunta — a IA foi instruída a não inventar.
            Sua resposta aqui vira conhecimento novo.
          </div>
        ))}

      <div className="sug-actions">
        {editing ? (
          <>
            <button
              className="btn pri sm"
              disabled={!draft.trim() || editOver}
              title={editOver ? `Passou ${draft.trim().length - limit!} caracteres do limite do canal` : undefined}
              onClick={() => { feedback('edited', draft); onUse(draft, false); setData(null) }}
            >
              Corrigir e enviar
            </button>
            <button className="btn ghost sm" onClick={() => { setEditing(false); setBeforePolish(null) }}>Cancelar</button>
            {/* "Melhorar com IA": deixa o texto mais cordial e claro com a conversa como contexto.
                Mesma conferência de fatos do aviso de cordialidade — se mexer em número ou link,
                a versão é descartada e o texto fica como estava. */}
            <span style={{ flex: 1 }} />
            {beforePolish !== null && (
              <button className="btn ghost sm" onClick={() => { setDraft(beforePolish); setBeforePolish(null) }}>
                Desfazer
              </button>
            )}
            <button
              className="btn sec sm"
              style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 5 }}
              disabled={!draft.trim() || polish.isPending}
              title="Deixa a resposta mais cordial e clara usando a conversa como contexto. Preços, prazos, links e números não mudam."
              onClick={() => polish.mutate(draft)}
            >
              <Icon name="spark" size={12} /> {polish.isPending ? 'Melhorando…' : 'Melhorar com IA'}
            </button>
          </>
        ) : (
          <>
            <button
              className="btn pri sm"
              disabled={blocked}
              title={blocked ? 'Corrija os problemas acima antes de enviar' : undefined}
              onClick={() => { feedback('sent_verbatim'); onUse(data.suggestion, true); setData(null) }}
            >
              Enviar
            </button>
            <button className="btn sec sm" onClick={() => setEditing(true)}>Editar</button>
            {/* Not "descartar": turning the card over is not a verdict on the suggestion. The
                verdict is recorded when a reply is actually sent from the other side. */}
            {onFlip && (
              <button className="btn ghost sm" onClick={onFlip}>Escrever eu mesmo</button>
            )}
            <button className="btn ghost sm" onClick={() => { feedback('rejected'); setData(null) }}>
              Descartar
            </button>
          </>
        )}
      </div>
    </div>
  )
}
